import { calculateProduction } from "@/planner/production";
import type { Scenario } from "@/schema/types";

/**
 * How much of a material a planet can send out per week.
 *
 * A route cannot ship more than the sending planet has to ship, and that is
 * what it makes plus anything that arrives for it from elsewhere, less what it
 * already sends on other routes. Without that last part two routes could each
 * claim the planet's whole output.
 *
 * What the planet burns itself is deliberately not subtracted. Shipping away
 * what you need locally is a legitimate choice that the resource balance will
 * then report as a shortfall, which is a more useful answer than a limit that
 * refuses to let the route be made.
 */

export type ShippableCap = {
  /** Made on the planet per week. */
  produced: number;
  /** Arriving by other active routes per week. */
  received: number;
  /** Already leaving on other active routes per week. */
  shippedElsewhere: number;
  /** What is left to put on this route. Never negative. */
  max: number;
};

function key(value: string): string {
  return value.trim().toUpperCase();
}

export function producedPerWeek(planet: Scenario["planets"][number], ticker: string): number {
  const wanted = key(ticker);
  if (!wanted) return 0;

  // Output depends only on batches, buildings and orders, so the rest of the
  // plan is left empty rather than dragging workforce data in for this.
  const plan = calculateProduction({
    batchInfos: planet.batchInfos,
    factories: planet.factories,
    produced: planet.produced,
    recipeInfos: [],
    workforceConsumption: [],
    needToBuy: [],
    incomingTradeRoutes: [],
    outgoingTradeRoutes: [],
  });

  return plan.produced.filter((row) => key(row.name) === wanted).reduce((sum, row) => sum + row.outputPerWeek, 0);
}

export function shippableCap(
  scenario: Scenario,
  fromPlanetId: string,
  ticker: string,
  /** The route being edited, so it does not count against itself. */
  excludeRouteId?: string,
): ShippableCap {
  const planet = scenario.planets.find((entry) => entry.id === fromPlanetId);
  const wanted = key(ticker);

  if (!planet || !wanted) return { produced: 0, received: 0, shippedElsewhere: 0, max: 0 };

  const active = scenario.tradeRoutes.filter((route) => route.enabled && key(route.resource) === wanted);

  const produced = producedPerWeek(planet, wanted);
  const received = active
    .filter((route) => route.toPlanetId === fromPlanetId && route.id !== excludeRouteId)
    .reduce((sum, route) => sum + route.amountPerWeek, 0);
  const shippedElsewhere = active
    .filter((route) => route.fromPlanetId === fromPlanetId && route.id !== excludeRouteId)
    .reduce((sum, route) => sum + route.amountPerWeek, 0);

  return {
    produced,
    received,
    shippedElsewhere,
    max: Math.max(0, produced + received - shippedElsewhere),
  };
}
